import React from 'react';
import { Tag, Typography, Tooltip, Button, Space } from 'antd';
import { PrinterOutlined, LineChartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { localizedName, displayValue, rangeLabel } from '../utils/labFlags';
import { FLAG_ICONS, FLAG_COLORS, flagText } from './VisitLabTestCards';
import './VisitLabTestCards.css';

const { Text } = Typography;

// A visit's lab tests, read-only: every value with its unit, the range it was
// judged against, and the flag. What the visit detail shows and what the print
// sheet is built from.
//
// Ranges and units come from the result's snapshot - what they were when the
// value was recorded - never from the live catalog. Labels are translated
// through the live field when the catalog still has it (the snapshot only
// carries English), and fall back to the snapshot.
//
//   rows       VisitLabTest documents for this visit, labTestId populated
//   fieldById  Map of live LabTestField rows by id (for translations)
//   sex        the patient's sex, so a sex-specific range prints as such
//   onTrend    (fieldId, label) -> open the trend view; omitted = not clickable
//   onPrint    () -> print this visit's sheet; omitted = no button

// The range a stored result was judged by. The snapshot carries the general
// range; the live field, if still around, tells us the sex-specific one.
const storedRange = (result, liveField, sex) => {
    if (liveField) return rangeLabel(liveField, sex);
    const snap = result.snapshot || {};
    return rangeLabel({
        dataType: snap.dataType,
        referenceRangeLow: snap.referenceRangeLow,
        referenceRangeHigh: snap.referenceRangeHigh,
        referenceText: snap.referenceText
    });
};

export const testTitle = (row, lang) => {
    const live = row.labTestId && typeof row.labTestId === 'object' ? row.labTestId : null;
    return live ? localizedName(live, lang) : (row.testSnapshot?.name || '');
};

export const fieldTitle = (result, liveField, lang) =>
    (liveField ? localizedName(liveField, lang) : (result.snapshot?.label || ''));

const VisitLabResults = ({ rows = [], fieldById = new Map(), sex, onTrend, onPrint, compact = false }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    if (!rows || rows.length === 0) return null;

    return (
        <div className="lab-results">
            {onPrint && (
                <div>
                    <Button size="small" icon={<PrinterOutlined />} onClick={onPrint}>
                        {t('printLabResults') || 'Print lab results'}
                    </Button>
                </div>
            )}
            {rows.map((row) => {
                const filled = (row.results || []).filter((r) => r.value !== null && typeof r.value !== 'undefined');
                const shown = compact ? filled : (row.results || []);
                return (
                    <div className="lab-results-test" key={row._id}>
                        <div className="lab-results-head">
                            <Space size={8}>
                                <Text strong>{testTitle(row, lang)}</Text>
                                <Tag color={row.status === 'resulted' ? 'green' : 'orange'} style={{ marginInlineEnd: 0 }}>
                                    {row.status === 'resulted'
                                        ? (t('labStatusResulted') || 'Resulted')
                                        : (t('labStatusOrdered') || 'Ordered')}
                                </Tag>
                            </Space>
                            {row.orderedAt && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    {new Date(row.orderedAt).toLocaleDateString()}
                                </Text>
                            )}
                        </div>
                        {shown.length > 0 ? (
                            <table className="lab-results-table">
                                <thead>
                                    <tr>
                                        <th>{t('labTest') || 'Test'}</th>
                                        <th>{t('result')}</th>
                                        <th>{t('labUnit') || 'Unit'}</th>
                                        <th>{t('referenceRange')}</th>
                                        <th>{t('labFlag') || 'Flag'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shown.map((result) => {
                                        const fieldId = String(result.labTestFieldId?._id || result.labTestFieldId);
                                        const live = fieldById.get(fieldId);
                                        const label = fieldTitle(result, live, lang);
                                        const value = displayValue(result.value, result.snapshot?.dataType, t);
                                        const empty = result.value === null || typeof result.value === 'undefined';
                                        const numeric = (result.snapshot?.dataType || live?.dataType) === 'number';
                                        return (
                                            <tr key={result._id || fieldId} className={result.flag ? `lab-results-row-${result.flag}` : ''}>
                                                <td>{label}</td>
                                                <td className={empty ? 'lab-results-empty' : 'lab-results-value'}>
                                                    {onTrend && numeric && !empty ? (
                                                        <Tooltip title={t('labTrend') || 'Show trend'}>
                                                            <button
                                                                type="button"
                                                                className="lab-results-value-button"
                                                                onClick={() => onTrend(fieldId, label)}
                                                            >
                                                                {value} <LineChartOutlined style={{ fontSize: 10 }} />
                                                            </button>
                                                        </Tooltip>
                                                    ) : value}
                                                </td>
                                                <td>{result.snapshot?.unit || live?.unit || ''}</td>
                                                <td>{storedRange(result, live, sex)}</td>
                                                <td>
                                                    {result.flag && (
                                                        <Tag color={FLAG_COLORS[result.flag]} icon={FLAG_ICONS[result.flag]} style={{ marginInlineEnd: 0 }}>
                                                            {flagText(t, result.flag)}
                                                        </Tag>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="lab-results-note">{t('labNoValuesYet') || 'No values entered yet.'}</div>
                        )}
                        {row.note && <div className="lab-results-note">{row.note}</div>}
                    </div>
                );
            })}
        </div>
    );
};

export default VisitLabResults;
